// **The type bar's editor** (`demo-presentation-shell` tasks 8.2/9.1-9.4). Chapter 2's whole
// "regular coding" beat: the presenter types a B++ program for a bee type and runs it, which
// compiles and hot-swaps it into the **running local world** through the C-1 double (contract
// C-1), taking effect at each bee's next decision tick, with no reconfigure or restart of the
// stage (`client-demo/running-a-typed-program-hot-swaps-it-into-the-live-world`).
//
// Reuses the shipped `CodeView` unchanged (design's own "reused with their existing harnesses"
// list), permanently in its `editing` mode — the type bar has no read-only state to toggle out
// of, unlike the live Colony screen's HandEdit. "Save" is this chapter's "Run".
//
// **Compile failure.** `DemoStageDriver.hotSwap` rejects on a C-1 refusal (both the fake and the
// worker-backed driver do — see `test/fake-demo-stage-driver.ts` and `demo.worker.ts`), so a
// failed compile never writes over the colony's live program: the world keeps executing whatever
// it already had (`.../a-failed-compile-surfaces-diagnostics-and-keeps-the-running-program`). The
// draft itself is left exactly as the presenter typed it — `CodeView`'s own "preserve the draft
// across a failed save" contract — so a diagnosed typo is still on screen to fix, not discarded.
//
// **The empty program.** An empty `source` is a normal, valid `hot_swap` on the fake and (by the
// same contract) the real C-1 surface — never the compile-failure path
// (`.../an-empty-program-leaves-its-bees-idle`). No special-casing is needed here for it beyond
// {@link demoTypeDocument}'s own: an empty draft crosses as an empty source, never as a bare
// header, which the real compiler refuses (`expected keyword 'state'`).
//
// **What the presenter types is a body; what the compiler wants is a document.** C-1 compiles a
// program with `compile_against` — the whole-type grammar, whose first line is
// `type <name> buffs <buff>` (`chapters.ts` and `stage-driver.ts`'s `DemoStageTypeSpec.program`
// both state this, and task 13.3 found every staged program headerless and refused). The script
// the presenter types on stage (`docs/PRESENTATION.md`, chapter 2) is a bare state list, because a
// header is a fact about the *type* — its name and its buffed characteristic — not about the
// behaviour being written, and the editor already knows both. So the editor composes the header
// rather than making the presenter type it: forwarding the published script verbatim refuses
// `compile_failed: expected keyword 'type'` and strands the chapter.

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { BeeTypeBar } from '../../ui/components/hud/BeeTypeBar';
import { CodeView } from '../../ui/components/script/CodeView';
import { abbrevForType, colorForType, spriteForType } from '../../ui/rounds/type-visuals';
import type { DemoStageDriver, DemoStageTypeSpec } from './stage-driver';

/** The complete type document C-1 compiles, from the type's identity and the presenter's body.
 * An empty body stays empty (see the module header). */
export function demoTypeDocument(name: string, buff: string, body: string): string {
  return body.trim().length === 0 ? '' : `type ${name} buffs ${buff}\n\n${body}`;
}

/** {@link demoTypeDocument}'s inverse, for opening the editor on a document the presenter did not
 * type — the Beekeeper's own source (`DemoApp`'s agent types). The editor's draft is the **body**,
 * because it composes the header itself, so a document handed back with its header still on would
 * compile to two of them. Anything without a header is already a body and passes through. */
export function demoTypeBody(source: string): string {
  return source.replace(/^type\s+\S+\s+buffs\s+\S+\n+/, '');
}

export interface TypeEditorProps {
  driver: DemoStageDriver;
  colony: number;
  type: string;
  /** The type's buffed characteristic — the second half of the document header the editor
   * composes. Must be the buff the colony registered the type with (`chapters.ts`), or the
   * compiler is checking the program against a type the sim does not have. */
  buff: string;
  /** The program already running for this type — the starting draft. Defaults to empty, which is
   * chapter 2's own starting point: the staged harvester idles, and the presenter types the
   * behaviour from a blank editor. This is the **body**, without the header the editor
   * composes. */
  initialProgram?: string;
  /**
   * Closes the editor. **`Cancel` dismisses; it does not discard** (E2E finding N6, where Cancel
   * was observed as a plain no-op and the panel then floated over chapter 3's world for the rest
   * of the show). Discarding would be the more orthodox `CodeView` reading, and it is the wrong
   * one here: the draft is a program the presenter typed in front of a room, and losing it to a
   * mis-aimed click is a worse outcome than a panel that reopens with text still in it.
   */
  onDismiss?: () => void;
}

export function TypeEditor({ driver, colony, type, buff, initialProgram = '', onDismiss }: TypeEditorProps): ReactElement {
  const [draft, setDraft] = useState(initialProgram);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);
  const [running, setRunning] = useState(false);

  async function run(): Promise<void> {
    setRunning(true);
    try {
      await driver.hotSwap({ colony, type, source: demoTypeDocument(type, buff, draft) });
      setDiagnostics([]);
    } catch (error) {
      setDiagnostics([error instanceof Error ? error.message : String(error)]);
    } finally {
      setRunning(false);
    }
  }

  return (
    // Left/right padding around the code input (2026-08-11 request): `CodeView`'s own editing-mode
    // textarea is flush against this panel's edges with no horizontal breathing room. `CodeView`
    // lives in `ui/**`, which this change does not own and every other screen using it must keep
    // looking exactly as it does, so the padding is added on this wrapper rather than in there.
    <div data-testid="demo-type-editor" style={{ padding: '0 16px' }}>
      <CodeView
        editable
        editing
        draft={draft}
        onDraftChange={setDraft}
        onSave={() => void run()}
        onCancel={() => onDismiss?.()}
        saveDisabled={running}
        diagnostics={diagnostics.length > 0 ? diagnostics : undefined}
      />
    </div>
  );
}

// -- the bar the editor lives in ----------------------------------------------------------------

/**
 * **The bee-type bar** (`docs/PRESENTATION.md` chapter 2: "the bee type bar … should show up with
 * the one bee type 'Harvester'").
 *
 * What shipped instead was a bare, permanently-open editor with no type identity on it at all — so
 * the audience never saw which type was being written, and (with `Cancel` inert) the dark panel
 * then sat over chapter 3's world, the fast-forward and the full-hive reveal (E2E finding N6).
 *
 * The bar is the **chips**; the editor is a panel one of them opens. Every chip keeps its own
 * editor mounted and merely hidden, so closing and reopening one does not cost the presenter the
 * program they typed into it. `dismissKey` closes whatever is open when it changes — the demo
 * passes the chapter, which is what stops chapter 2's panel from following the show into chapter 3.
 */
export interface TypeBarProps {
  driver: DemoStageDriver;
  colony: number;
  /** The colony's registered types, in registration order. Read off the stage the cursor's chapter
   * configured, or — once a Beekeeper turn has been accepted — off the live colony (`DemoApp`);
   * never restated here, so a rename in `chapters.ts` cannot leave a chip naming a type the colony
   * does not have.
   *
   * `program` is the **whole type document** and is optional: supplied, the chip's editor opens on
   * its body, which is what makes the Beekeeper's own builder readable the moment its chip appears;
   * omitted (the staged types), the editor opens blank, which is chapter 2's beat — the presenter
   * types the program in front of the room.
   *
   * `count` is the type's living population at the latest snapshot, or absent/`null` before one
   * has arrived — rendered as the game's own em-dash placeholder, never an invented 0. */
  types: readonly (Pick<DemoStageTypeSpec, 'name' | 'buff'> &
    Partial<Pick<DemoStageTypeSpec, 'program'>> & { count?: number | null })[];
  /** Any value whose change means "the show has moved on" — the open panel closes. */
  dismissKey?: string;
}

export function TypeBar({ driver, colony, types, dismissKey }: TypeBarProps): ReactElement {
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => setOpen(null), [dismissKey]);
  // Insertion-ordered, and a ref rather than state for the same reason `DemoTournamentChapters`
  // keeps one: it is appended to during render and read in the same pass. An editor is mounted
  // the first time its chip is opened and stays mounted (hidden) thereafter, so nothing that was
  // never opened is on the projector and nothing that was is ever emptied.
  const mounted = useRef<string[]>([]);
  if (open !== null && !mounted.current.includes(open)) {
    mounted.current.push(open);
  }

  return (
    <div data-testid="demo-type-bar">
      {/* The game's own bee-type dock (2026-08-09 request: normal game UI, with sprites and
          living counts, instead of a demo-only text chip). Same component, same visuals helpers
          the Sandbox screen binds; centred rather than stretched because the demo has no layout
          stack for it to fill. */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <BeeTypeBar
          types={types.map((type, index) => ({
            name: type.name,
            count: type.count ?? null,
            color: colorForType(type.name, index),
            sprite: spriteForType(type.name),
            abbrev: abbrevForType(type.name),
          }))}
          selectedName={open ?? undefined}
          onSelect={(name) => setOpen((current) => (current === name ? null : name))}
        />
      </div>
      {mounted.current.map((name) => {
        const type = types.find((candidate) => candidate.name === name);
        if (type === undefined) return null;
        return (
          // Hidden, never unmounted: an accidental `Cancel` must not cost the typed program.
          <div
            key={name}
            data-testid={`demo-type-panel-${name}`}
            hidden={name !== open}
            style={name === open ? undefined : { display: 'none' }}
          >
            <TypeEditor
              driver={driver}
              colony={colony}
              type={type.name}
              buff={type.buff}
              initialProgram={type.program === undefined ? undefined : demoTypeBody(type.program)}
              onDismiss={() => setOpen(null)}
            />
          </div>
        );
      })}
    </div>
  );
}
